import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import 'react-quill/dist/quill.snow.css';
import './PrescriptionForm.css';
import { PDFDownloadLink } from '@react-pdf/renderer';
import PrescriptionPDF from './PrescriptionPDF';
import { getAuth } from 'firebase/auth';
import { gerarCodigo } from '../../services/codigoReceitaService';
import { db } from '../../firebase';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  setDoc,
} from 'firebase/firestore';
function PrescriptionForm({ observacao }) {
  const [crm, setCrm] = useState('');
  const [estadoCrm, setEstadoCrm] = useState('');
  const [titulo, setTitulo] = useState('');
  const [nome, setNome] = useState('');
  const [cpfPaciente, setCpfPaciente] = useState('');
  const [enderecoPaciente, setEnderecoPaciente] = useState('');
  const [validadeReceita, setValidadeReceita] = useState('');
  const [dataAtendimento, setDataAtendimento] = useState('');
  const [medicamento, setMedicamento] = useState('');
  const [formaUso, setFormaUso] = useState('');
  const [posologia, setPosologia] = useState('');
  const [tipoReceita, setTipoReceita] = useState('normal');
  const [nomeComprador, setNomeComprador] = useState('');
  const [cpfComprador, setCpfComprador] = useState('');
  const [enderecoComprador, setEnderecoComprador] = useState('');
  const [isFormValid, setIsFormValid] = useState(false);
  const [clinicaData, setClinicaData] = useState({});
  const [pacientes, setPacientes] = useState([]); // Estado para armazenar os pacientes
  const [nomePaciente, setNomePaciente] = useState(''); // Agora definido corretamente

  const auth = getAuth();
  const user = auth.currentUser;

  const [tokenReceita, setTokenReceita] = useState('');

  const generateAndStoreToken = async () => {
    if (user && crm) {
      try {
        const generatedToken = await gerarCodigo(user.uid);
        setTokenReceita(generatedToken);

        const receitaRef = doc(db, 'receitasDigitalGeradas', generatedToken);
        await setDoc(receitaRef, {
          userId: user.uid,
          nomeMedico: nome,
          tituloMedico: titulo,
          token: generatedToken,
          dataCriacao: new Date().toISOString(),
        });
      } catch (error) {
        console.error('Erro ao gerar e armazenar o token da receita:', error);
      }
    }
  };

  useEffect(() => {
    const fetchProfessionalData = async () => {
      if (user) {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          setCrm(userDoc.data().crm || '');
          setEstadoCrm(userDoc.data().estadoCrm || '');
          setNome(userDoc.data().nome || '');
          setTitulo(userDoc.data().titulo || '');

          const enderecoComercial = userDoc.data().enderecoComercial;
          if (enderecoComercial) {
            setClinicaData({
              ...userDoc.data(),
              endereco: enderecoComercial,
            });
          } else {
            setClinicaData({
              ...userDoc.data(),
              endereco: '',
            });
          }
        }
      }
    };
    const fetchPacientes = async () => {
      if (user) {
        try {
          const q = query(
            collection(db, 'pacientes'),
            where('userId', '==', user.uid)
          );
          const querySnapshot = await getDocs(q);
          setPacientes(querySnapshot.docs.map((doc) => doc.data().nome));
        } catch (error) {
          console.error('Erro ao buscar pacientes:', error);
        }
      }
    };

    fetchPacientes();
    fetchProfessionalData();
  }, [user]);

  useEffect(() => {
    const fetchPacienteData = async () => {
      if (nomePaciente) {
        try {
          const q = query(
            collection(db, 'pacientes'),
            where('nome', '==', nomePaciente)
          );
          const querySnapshot = await getDocs(q);

          if (!querySnapshot.empty) {
            const pacienteData = querySnapshot.docs[0].data();
            setCpfPaciente(pacienteData.cpf || '');
            setEnderecoPaciente(pacienteData.endereco || '');

            if (tipoReceita === 'especial') {
              setNomeComprador(nomePaciente || '');
              setCpfComprador(pacienteData.cpf || '');
              setEnderecoComprador(pacienteData.endereco || '');
            }
          }
        } catch (error) {
          console.error('Erro ao buscar dados do paciente:', error);
        }
      }
    };

    fetchPacienteData();
  }, [nomePaciente, tipoReceita]);

  useEffect(() => {
    const checkFormValidity = () => {
      if (medicamento && formaUso && posologia && nome) {
        if (tipoReceita === 'especial') {
          if (nomeComprador && cpfComprador && enderecoComprador) {
            setIsFormValid(true);
          } else {
            setIsFormValid(false);
          }
        } else {
          setIsFormValid(true);
        }
      } else {
        setIsFormValid(false);
      }
    };

    checkFormValidity();
  }, [medicamento, formaUso, posologia, tipoReceita, nome]);

  useEffect(() => {
    if (isFormValid) {
      generateAndStoreToken();
    }
  }, [isFormValid]);

  const pdfData = {
    nomePaciente,
    cpfPaciente,
    enderecoPaciente,
    clinica: clinicaData,
    dataAtendimento,
    crm,
    estadoCrm,
    titulo,
    nome,
    validadeReceita,
    medicamento,
    formaUso,
    posologia,
    observacao,
    tipoReceita,
    nomeComprador,
    cpfComprador,
    enderecoComprador,
    tokenReceita,
  };

  return (
    <div>
      <h2 tabIndex="0" className="font-bold mb-4">
        Gerar Receita Médica
      </h2>
      <div className="receita-page">
        <form
          className="receita-form"
          aria-labelledby="form-title"
          onSubmit={(e) => e.preventDefault()}
        >
          {/* Seção de Informações do Paciente */}
          <fieldset className="form-section">
            <legend>Informações do Paciente</legend>
            <div className="input-group">
              <label htmlFor="nomePaciente">Nome do Paciente:</label>
              <Autocomplete
                freeSolo
                id="nomePaciente"
                options={pacientes}
                value={nomePaciente}
                onChange={(e, newValue) => setNomePaciente(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id="nomePaciente"
                    name="nomePaciente"
                    placeholder="Nome do Paciente"
                    variant="outlined"
                    required
                  />
                )}
              />
            </div>
            <div className="input-group">
              <label htmlFor="cpfPaciente">CPF do Paciente:</label>
              <input
                type="text"
                id="cpfPaciente"
                name="cpfPaciente"
                placeholder="Digite o CPF do Paciente"
                aria-required="false"
                value={cpfPaciente}
                onChange={(e) => setCpfPaciente(e.target.value)}
              />
            </div>
            <div className="input-group">
              <label htmlFor="enderecoPaciente">Endereço do Paciente:</label>
              <input
                type="text"
                id="enderecoPaciente"
                name="enderecoPaciente"
                placeholder="Endereço do Paciente"
                aria-required="false"
                value={enderecoPaciente}
                onChange={(e) => setEnderecoPaciente(e.target.value)}
              />
            </div>
            <div className="input-group">
              <label htmlFor="dataAtendimento">
                Data e Hora do Atendimento:
              </label>
              <input
                type="date"
                id="dataAtendimento"
                name="dataAtendimento"
                aria-required="true"
                value={dataAtendimento}
                onChange={(e) => setDataAtendimento(e.target.value)}
              />
            </div>
          </fieldset>

          {/* Seção de Receita */}
          <fieldset className="form-section">
            <legend>Detalhes da Receita</legend>
            <div className="input-group">
              <label htmlFor="tipoReceita">Tipo de Receita:</label>
              <select
                id="tipoReceita"
                name="tipoReceita"
                value={tipoReceita}
                onChange={(e) => setTipoReceita(e.target.value)}
                aria-required="true"
              >
                <option value="normal">Normal</option>
                <option value="especial">Controle Especial</option>
              </select>
            </div>
            <div className="input-group">
              <label htmlFor="validadeReceita">Validade da Receita:</label>
              <input
                type="date"
                id="validadeReceita"
                name="validadeReceita"
                placeholder="Validade da Receita"
                aria-required="false"
                value={validadeReceita}
                onChange={(e) => setValidadeReceita(e.target.value)}
              />
            </div>
          </fieldset>

          {/* Seção de Medicação */}
          <fieldset className="form-section">
            <legend>Informações da Medicação</legend>
            <div className="input-group">
              <label htmlFor="formaUso">Apresentação:</label>
              <input
                type="text"
                id="formaUso"
                name="formaUso"
                placeholder="Forma de Uso"
                aria-required="true"
                value={formaUso}
                onChange={(e) => setFormaUso(e.target.value)}
              />
            </div>
            <div className="input-group">
              <label htmlFor="medicamento">
                Nome do Medicamento e Quantidade:
              </label>
              <input
                type="text"
                id="medicamento"
                name="medicamento"
                placeholder="Nome do Medicamento e Quantidade"
                aria-required="true"
                value={medicamento}
                onChange={(e) => setMedicamento(e.target.value)}
              />
            </div>
            <div className="input-group">
              <label htmlFor="posologia">Posologia:</label>
              <textarea
                id="posologia"
                name="posologia"
                placeholder="Instruções de Posologia"
                rows="4"
                aria-required="true"
                value={posologia}
                onChange={(e) => setPosologia(e.target.value)}
              ></textarea>
            </div>
          </fieldset>

          {tipoReceita === 'especial' && (
            <fieldset className="form-section">
              <legend>Informações do Comprador</legend>
              <div className="comprador-group">
                <div className="input-group">
                  <label htmlFor="nomeComprador">Nome do Comprador:</label>
                  <input
                    type="text"
                    id="nomeComprador"
                    name="nomeComprador"
                    placeholder="Nome do Comprador"
                    aria-required="false"
                    value={nomeComprador}
                    onChange={(e) => setNomeComprador(e.target.value)}
                  />
                </div>
                <div className="input-group">
                  <label htmlFor="cpfComprador">CPF do Comprador:</label>
                  <input
                    type="text"
                    id="cpfComprador"
                    name="cpfComprador"
                    placeholder="CPF do Comprador"
                    aria-required="false"
                    value={cpfComprador}
                    onChange={(e) => setCpfComprador(e.target.value)}
                  />
                </div>
                <div className="input-group">
                  <label htmlFor="enderecoComprador">
                    Endereço do Comprador:
                  </label>
                  <input
                    type="text"
                    id="enderecoComprador"
                    name="enderecoComprador"
                    placeholder="Endereço do Comprador"
                    aria-required="false"
                    value={enderecoComprador}
                    onChange={(e) => setEnderecoComprador(e.target.value)}
                  />
                </div>
              </div>
            </fieldset>
          )}

          <PDFDownloadLink
            document={<PrescriptionPDF data={pdfData} />}
            fileName="Receita_Medica.pdf"
          >
            {({ loading }) => (
              <button
                type="button"
                className="px-4 py-2 rounded-md shadow-md text-base font-medium transition transform duration-300 ease-in-out focus:outline-none focus:ring-4 focus:ring-opacity-50 active:scale-95 undefined  bg-blue-600 text-white font-bold py-2 px-4 rounded hover:bg-blue-700 transition duration-200"
                aria-label="Gerar Receita Médica"
                disabled={!isFormValid || loading}
              >
                {loading ? 'Gerando PDF...' : 'Gerar Receita'}
              </button>
            )}
          </PDFDownloadLink>

          {!isFormValid && (
            <div className="form-warning">
              <p>Preencha todos os campos obrigatórios para gerar a receita.</p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
PrescriptionForm.propTypes = {
  observacao: PropTypes.string.isRequired,
};

export default PrescriptionForm;
